<template>
  <div class="space-y-5 pb-8 text-foxy-text">
    <publications-nav :profile="profile" :dashboard="dashboard" />

    <form class="flex flex-col gap-2.5 sm:flex-row sm:items-center" aria-label="Пошук і впорядкування фактів" @submit.prevent="runSearchNow">
      <label class="min-w-0 flex-1">
        <span class="sr-only">Пошук за фактом або назвою книги</span>
        <input v-model="searchQuery" type="search" class="foxy-input w-full !py-2.5" placeholder="Пошук за фактом або назвою книги...">
      </label>
      <label class="w-full sm:w-36 sm:shrink-0">
        <span class="sr-only">Сортування фактів</span>
        <select v-model="sort" class="foxy-select w-full !py-2.5">
          <option value="newest">Новіші</option>
          <option value="oldest">Старіші</option>
        </select>
      </label>
    </form>

    <p v-if="searchQuery.trim()" class="text-sm text-foxy-text-secondary" role="status" aria-live="polite">
      {{ loading || searchPending ? 'Шукаємо…' : `Знайдено: ${matchingTotal}` }}
    </p>
    <p v-if="notice.message" class="rounded-xl border p-3 text-sm" role="status"
      :class="notice.type === 'error' ? 'border-foxy-error/40 text-foxy-error' : 'border-foxy-border text-foxy-text'">
      {{ notice.message }}
    </p>
    <div v-if="loadError" class="rounded-xl border border-foxy-error/40 p-4 text-sm text-foxy-error" role="alert">
      {{ loadError }}
      <button type="button" class="acc-btn ml-2" :disabled="loading" @click="reloadArchive">Спробувати ще</button>
    </div>
    <p v-if="loading && !facts.length" class="py-8 text-sm text-foxy-text-secondary">Завантаження фактів…</p>
    <p v-else-if="!loading && !searchPending && !loadError && !facts.length" class="py-8 text-sm text-foxy-text-secondary">
      {{ searchQuery.trim() ? 'За цим пошуком нічого не знайдено.' : 'У профілі поки немає опублікованих фактів.' }}
    </p>

    <div v-if="facts.length" class="space-y-3" :aria-busy="loading ? 'true' : 'false'">
      <AccountFactCard v-for="fact in facts" :key="fact.id" :fact="fact" :publication-context="publicationContext"
        @updated="handleUpdated" @deleted="handleDeleted" @notice="showNotice"
        @publication-mutation-start="$emit('publication-mutation-start', $event)"
        @publication-mutation-finish="$emit('publication-mutation-finish', $event)" />
    </div>
    <div v-if="hasMore" class="flex justify-center pt-2">
      <button type="button" class="acc-btn-primary" :disabled="loading || loadingMore || searchPending" @click="loadMore">
        {{ loadingMore ? 'Завантаження…' : 'Показати ще' }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import axios from 'axios';
import PublicationsNav from '../components/PublicationsNav.vue';
import AccountFactCard from '../components/AccountFactCard.vue';

export default {
  name: 'ProfileFacts',
  components: { PublicationsNav, AccountFactCard },
  props: {
    profile: { type: Object, required: true },
    publicationContext: { type: Object, default: null },
    dashboard: { type: Object, default: null },
  },
  data() {
    return {
      facts: [], matchingTotal: 0, searchQuery: '', sort: 'newest',
      currentPage: 0, lastPage: 1, loading: false, loadingMore: false,
      searchPending: false, loadError: '', debounceTimer: null, requestSequence: 0,
      notice: { message: '', type: 'success' }, noticeTimer: null,
    };
  },
  computed: {
    hasMore() { return this.currentPage > 0 && this.currentPage < this.lastPage; },
    requestContext() { return JSON.stringify([this.profile.username, this.searchQuery.trim(), this.sort]); },
  },
  watch: {
    'profile.username'() { this.reloadArchive(); },
    searchQuery() { this.scheduleReset(); },
    sort() { this.scheduleReset(0); },
  },
  mounted() { this.reloadArchive(); },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
    clearTimeout(this.noticeTimer);
    this.requestSequence += 1;
  },
  methods: {
    invalidateContinuation() {
      this.requestSequence += 1;
      this.currentPage = 0;
      this.lastPage = 1;
      this.loadingMore = false;
      this.searchPending = true;
    },
    scheduleReset(delay = 275) {
      clearTimeout(this.debounceTimer);
      this.invalidateContinuation();
      this.debounceTimer = setTimeout(() => this.fetchFacts(1), delay);
    },
    runSearchNow() { return this.reloadArchive(); },
    reloadArchive() {
      clearTimeout(this.debounceTimer);
      this.invalidateContinuation();
      return this.fetchFacts(1);
    },
    async fetchFacts(page) {
      const requestId = ++this.requestSequence;
      const context = this.requestContext;
      const firstPage = page === 1;
      this.loadError = '';
      if (firstPage) { this.loading = true; this.loadingMore = false; }
      else this.loadingMore = true;
      try {
        const response = await axios.get(`/api/account/profile/${encodeURIComponent(this.profile.username)}/quotes-facts`, {
          params: { page, type: 'facts', sort: this.sort, q: this.searchQuery.trim() || undefined },
        });
        if (requestId !== this.requestSequence || context !== this.requestContext) return;
        const rows = (response.data.data || []).filter((item) => item.type === 'fact');
        const merged = firstPage ? rows : [...this.facts, ...rows];
        this.facts = Array.from(new Map(merged.map((item) => [item.id, item])).values());
        this.currentPage = Number(response.data.meta?.current_page || page);
        this.lastPage = Number(response.data.meta?.last_page || 1);
        this.matchingTotal = Number(response.data.meta?.total || 0);
      } catch (error) {
        if (requestId !== this.requestSequence || context !== this.requestContext) return;
        this.loadError = error.response?.data?.message || 'Не вдалося завантажити факти.';
      } finally {
        if (requestId === this.requestSequence && context === this.requestContext) {
          this.loading = false;
          this.loadingMore = false;
          this.searchPending = false;
        }
      }
    },
    loadMore() {
      if (!this.loading && !this.loadingMore && !this.searchPending && this.hasMore) return this.fetchFacts(this.currentPage + 1);
    },
    handleUpdated(updated) {
      const index = this.facts.findIndex((item) => item.id === updated.id);
      if (index !== -1) this.$set(this.facts, index, { ...this.facts[index], ...updated });
      return this.reloadArchive();
    },
    handleDeleted(id, factsCount, context) {
      if (this._isDestroyed || !context || context.username !== this.profile.username
        || context.generation !== this.publicationContext?.generation) return;
      this.facts = this.facts.filter((item) => item.id !== id);
      this.$emit('fact-deleted', { ...context, factsCount });
      return this.reloadArchive();
    },
    showNotice(message, type = 'success') {
      this.notice = { message, type };
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => { this.notice = { message: '', type: 'success' }; }, 4000);
    },
  },
};
</script>
